import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import {
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  useLibraryAssetsQuery,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import {
  isPartsContent,
  toDraft,
  toPartsContent,
  type DraftPart,
} from "@/lib/assetContent";
import { libraryAssetHref } from "@/lib/librarySections";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Select } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { createFieldErrors } from "@/ui/library/NewAssetDialog";
import { LoadingState, UnavailableState } from "@/ui/states";

import {
  NamedReadFailureState,
  type NamedReadFailure,
} from "./NamedReadFailure";

/**
 * Create a Pathway family default, or a variant under one (#643).
 *
 * The generic `NewAssetDialog` reaches this family through a Type picker and
 * authors its v1 through `PartsEditor`, whose free-text "Part key" input is the
 * manual-key escape hatch the 2026-08-25 amendment forbids and whose structured
 * field is a writable textarea over raw JSON — a mechanism the ratified product
 * contract lists among the rejected ones. So Pathways joins main concern in
 * `CANONICAL_OWN_DIALOG` and creates through this instead: the TYPE is decided
 * by the control that opened the dialog, and v1 copies a service-stored exact
 * version rather than asking the clinician to fabricate protected documents.
 *
 * ## The two fields that are not a name, and why each is here
 *
 *   * **Parent pathway**, for a variant only. `parentPathwayId` is required for
 *     `pathway_variant` and refused on anything else, and the contract states
 *     variant identity is "explicit, never a name". It is a select over the
 *     ACTIVE `pathway` rows, because the service refuses an archived parent with
 *     `field: "parentPathwayId"` — offering one would be offering a choice
 *     already known to fail.
 *   * **Pack placement.** `placementCoordinate` is "explicit, never derived from
 *     the name, and immutable once stored". Deriving it would contradict the
 *     contract; omitting it would make every creation fail with a refusal
 *     pointing at a field nothing asked the clinician to fill in. It is the same
 *     single divergence `NewMainConcernDialog` records.
 *
 * REQUIRED FOR A DEFAULT, OPTIONAL FOR A VARIANT, and that asymmetry is the
 * service's rather than a convenience: a variant's coordinate is "the parent
 * pathway's own coordinate, then '/', then this variant's slug", and a variant
 * "under a seeded or otherwise unplaced parent is created without one". Making
 * it mandatory here would make a variant of base canon — whose parent is placed
 * by the pack manifest and carries no coordinate of its own — uncreatable in the
 * browser, which is exactly the trap #366 recorded on the generic dialog.
 *
 * EXACT STARTING CONTENT IS REQUIRED. The service has no draft-only library
 * lifecycle: create publishes immutable v1 immediately, and that version may be
 * adopted. Publishing either an "unwritten" prompt or an empty protected
 * document would therefore make placeholder content clinical content. The
 * service also does not publish semantic schemas for protected steps, so this
 * dialog cannot author them safely. Every new asset instead starts from an
 * exact immutable version of an existing asset of the same type. That preserves
 * valid protected content and leaves subsequent prompt editing to the dedicated
 * named-step surface.
 *
 * AND SINCE #954 THAT SOURCE IS SENT, not merely relied on. The service now
 * refuses a create carrying a document at a role it publishes no schema for
 * unless the request names the immutable version it was copied from and the
 * bytes match. This dialog already held exactly that pair — the asset it read
 * and the version it read it AT — so the change is one field, and the workflow
 * it protects is the one this dialog exists for: "Create editable copy" of a
 * seeded ten-file Pathway keeps working, and an invented classification schema
 * no longer publishes at v1. A from-scratch variant is unaffected, because it
 * copies too.
 */

const NAME_MAX_LENGTH = 60;

/**
 * What a refusal computed in the BROWSER says, so nothing is ever swallowed.
 *
 * This dialog builds the whole parts array and hands it to `toPartsContent`
 * before publishing, and that check can refuse for reasons no field on screen
 * owns. A silent `return` there is what makes a Create button a dead control,
 * so every path out of this submit leaves a sentence behind.
 */
const REFUSED_CONTENT =
  "This pathway's steps could not be prepared, so nothing was created.";

export type NewPathwayType = "pathway" | "pathway_variant";

/**
 * What a copy needs to know about its source, and nothing else.
 *
 * Narrower than `LibraryAsset` on purpose. Two surfaces open this dialog as a
 * copy — the asset page, which holds a catalog row, and the catalog, which holds
 * a `PathwayFamilyDefault`/`PathwayFamilyVariant` from the family read — and
 * those two responses carry different fields. Widening to `LibraryAsset` would
 * force the catalog to invent a `createdBy` and an `updatedAt` the family
 * response does not carry, which is fabricated provenance in a dialog whose job
 * is to preserve exact identity. Declaring the four fields this dialog actually
 * reads lets both callers pass what they really have.
 */
export type PathwayCopySource = {
  readonly id: string;
  readonly name: string;
  /** The exact immutable version a copy reads its content from. */
  readonly latestVersion: string;
} & (
  | { readonly assetType: "pathway" }
  | {
      readonly assetType: "pathway_variant";
      /** Prefills the parent, which a variant's copy is still a child of. */
      readonly parentPathwayId: string;
    }
);

/**
 * What this dialog is waiting on, or why it cannot proceed.
 */
type PrerequisitePurpose = "source" | "catalog";

type Prerequisite =
  | { state: "pending"; purpose: PrerequisitePurpose }
  | ({ state: "failed"; purpose: PrerequisitePurpose } & NamedReadFailure)
  | { state: "ready" };

/**
 * One query's read state as a prerequisite, or `null` when it has succeeded.
 *
 * `null` rather than a `"ready"` member so the caller can compose the catalog
 * and exact-version reads. Paused is tested FIRST — a retry held by the retryer
 * keeps `isPending` true with `isError` false, and testing pending first is what
 * puts a permanent skeleton over a failure the Editor Service already sent.
 */
function readState(
  query: {
    isPaused: boolean;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    failureReason: unknown;
  },
  retry: () => void,
  purpose: PrerequisitePurpose,
): Prerequisite | null {
  if (query.isPaused) {
    return {
      state: "failed",
      purpose,
      failure: query.failureReason,
      isPaused: true,
      retry,
    };
  }
  if (query.isError) {
    return {
      state: "failed",
      purpose,
      failure: query.error,
      isPaused: false,
      retry,
    };
  }
  if (query.isPending) {
    return { state: "pending", purpose };
  }
  return null;
}

export function NewPathwayDialog({
  assetType,
  onClose,
  copyFrom,
  /**
   * A parent chosen before the dialog opened — the family page's "New variant".
   *
   * Prefills and is still editable: the clinician opened the dialog from one
   * family, which is evidence about intent rather than a constraint the service
   * enforces.
   */
  parentPathwayId: initialParent = "",
}: {
  assetType: NewPathwayType;
  onClose: () => void;
  /** Opens as "create an editable copy of this asset". */
  copyFrom?: PathwayCopySource;
  parentPathwayId?: string;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  // Active rows only: an archived parent is refused by the service, so listing
  // one would offer a choice already known to fail. The same read supplies the
  // exact-version starting-content choices for a new asset.
  const catalog = useLibraryAssetsQuery(false);
  const nameErrorId = useId();
  const placementErrorId = useId();
  const parentErrorId = useId();
  const sourceErrorId = useId();

  const isVariant = assetType === "pathway_variant";

  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (copy)` : "");
  const [sourceId, setSourceId] = useState(copyFrom?.id ?? "");
  const [placementCoordinate, setPlacementCoordinate] = useState("");
  const [parentPathwayId, setParentPathwayId] = useState(
    copyFrom?.assetType === "pathway_variant"
      ? copyFrom.parentPathwayId
      : initialParent,
  );
  const [blocked, setBlocked] = useState<string | null>(null);

  const assets = catalog.data?.items ?? [];
  const sourceCandidates = assets.filter(
    (candidate) => candidate.assetType === assetType,
  );
  // A direct copy may start from an archived seeded asset, which is deliberately
  // absent from the active catalog. A normal New journey chooses only an active
  // same-type row from the service response.
  const sourceAsset =
    (copyFrom?.id === sourceId ? copyFrom : null) ??
    sourceCandidates.find((candidate) => candidate.id === sourceId) ??
    null;

  // Every create carries the source's exact immutable content. The placement is
  // deliberately left blank: a coordinate is unique among ACTIVE assets of its
  // type, so pre-filling the source's offers a value guaranteed to be refused.
  const source = useAssetVersionQuery(
    sourceAsset?.id ?? "",
    sourceAsset ? sourceAsset.latestVersion : null,
  );

  // Both recoveries are cancel-then-refetch. A bare `refetch()` fires nothing
  // in exactly the state that offers it — a held retry answers with its own
  // existing promise (#215).
  const retrySource = useRetryPausedQuery(
    queryKeys.assetVersion(
      sourceAsset?.id ?? "",
      sourceAsset?.latestVersion ?? "",
    ),
  );
  const retryCatalog = useRetryPausedQuery(queryKeys.libraryAssets(false));

  const parents = assets.filter(
    (asset) => asset.assetType === "pathway",
  );
  const parentUnavailable =
    isVariant &&
    parentPathwayId !== "" &&
    catalog.isSuccess &&
    !parents.some((pathway) => pathway.id === parentPathwayId);
  const sourceUnavailable =
    !copyFrom &&
    sourceId !== "" &&
    catalog.isSuccess &&
    sourceAsset === null;

  const fieldErrors = create.isError ? createFieldErrors(create.error) : {};
  // A FIELD ERROR IS ONLY ATTRIBUTABLE WHERE ITS CONTROL IS ON SCREEN, and the
  // Starting content picker exists only in the NEW journey: a direct copy has no
  // source control, because the source IS the row the clinician pressed Copy on.
  //
  // So a `copiedFrom` refusal (#954) lands on that control when it is rendered
  // and falls through to the notice when it is not. Folding it into
  // `fieldErrors` unconditionally made `Object.keys(...).length === 0` false in
  // copy mode, which suppressed the notice while nothing rendered the message —
  // a refusal displayed nowhere, which is the one outcome every path out of this
  // submit is written to prevent.
  const sourceError = copyFrom ? undefined : fieldErrors.copiedFrom;
  const unattributedSource = copyFrom ? fieldErrors.copiedFrom : undefined;
  const attributedCount =
    Object.keys(fieldErrors).length - (unattributedSource === undefined ? 0 : 1);
  const generalMessage =
    blocked ??
    unattributedSource ??
    (create.isError && attributedCount === 0
      ? create.error instanceof ApiError
        ? create.error.message
        : "The Editor Service could not be reached, so nothing was created."
      : null);

  const busy = create.isPending;
  const catalogPrerequisite =
    !copyFrom || isVariant
      ? readState(catalog, retryCatalog, "catalog")
      : null;
  const sourcePrerequisite =
    sourceAsset === null
      ? null
      : readState(source, retrySource, "source");
  const prerequisite: Prerequisite =
    catalogPrerequisite ?? sourcePrerequisite ?? { state: "ready" };

  function submit() {
    setBlocked(null);

    const read = source.isSuccess ? source.data : null;
    if (sourceAsset === null || read === null || !isPartsContent(read.content)) {
      // Its own sentence rather than the generic refusal below: "at least one
      // part" would describe an empty draft, when what actually happened is
      // that the source came back in a shape this build does not model.
      setBlocked(
        `The Editor Service did not return usable starting content for this ${typeLabel}, so nothing was created.`,
      );
      return;
    }
    const parts: DraftPart[] = toDraft(read.content);

    const content = toPartsContent(parts);
    if (!content.ok) {
      // NEVER A SILENT RETURN: the clinician pressed Create and is owed an
      // answer, whatever the remaining refusal is.
      setBlocked(content.problems[0]?.message ?? REFUSED_CONTENT);
      return;
    }

    const placement = placementCoordinate.trim();
    create.mutate(
      {
        assetType,
        name: name.trim(),
        content: content.content,
        // THE PAIR THE CONTENT ITSELF CAME BACK UNDER, and that is the whole of
        // what this field claims (#954). Naming it lets the service check the
        // engine-owned documents among the parts above against the library's own
        // copy instead of taking them on trust.
        //
        // READ OFF THE VERSION RESPONSE, not off the catalog row. The two agree
        // in the ordinary case — the query is keyed on `latestVersion` — but the
        // catalog can refetch and move `latestVersion` on while the served
        // version detail in hand stays the one the bytes came from. Sending the
        // catalog's label would then claim provenance the content does not have,
        // and the service would refuse a copy that is in fact exact. The
        // response answers for itself.
        copiedFrom: { assetId: read.assetId, version: read.version },
        // The SAME version `copiedFrom` names, for the same reason: the human
        // sentence and the checked reference must not describe two different
        // versions of one copy.
        note: `Started from "${sourceAsset.name}" at ${read.version}.`,
        ...(isVariant ? { parentPathwayId } : {}),
        // Sent only when the clinician typed one. An empty string is not a
        // coordinate, and a variant under an unplaced parent is created without
        // any — the service refuses an empty one rather than ignoring it.
        ...(placement === "" ? {} : { placementCoordinate: placement }),
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(libraryAssetHref(created.assetType, created.id));
        },
      },
    );
  }

  const typeLabel = isVariant ? "pathway variant" : "pathway";

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={copyFrom ? "Create editable copy" : `New ${typeLabel}`}
        description={
          copyFrom
            ? `Creates your own ${typeLabel} starting from this one. The original stays exactly as it is.`
            : isVariant
              ? "A versioned variant under an explicit family default. Choose an existing variant as exact starting content; author changes afterwards."
              : "A family default: the parent whose variants a project pins alongside it. Choose an existing pathway as exact starting content; author changes afterwards."
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={
                busy ||
                name.trim() === "" ||
                // A DEFAULT'S COORDINATE IS UNCONDITIONALLY REQUIRED; A
                // VARIANT'S IS NOT. See the header: a variant under a seeded or
                // otherwise unplaced parent is created without one, and gating
                // on it here would make base canon's children uncreatable.
                (!isVariant && placementCoordinate.trim() === "") ||
                (isVariant &&
                  (parentPathwayId === "" || parentUnavailable)) ||
                sourceId === "" ||
                sourceUnavailable ||
                prerequisite.state !== "ready"
              }
              onClick={submit}
            >
              {copyFrom ? "Create copy" : `Create ${typeLabel}`}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label="Name"
            description={`Short name for the ${typeLabel}. Additional steps are authored afterwards from the ${typeLabel}'s own page.`}
          >
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  placeholder={
                    isVariant
                      ? "e.g. Agoraphobia, Panic disorder"
                      : "e.g. Anxiety, Depression"
                  }
                  aria-invalid={fieldErrors.name !== undefined || undefined}
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    fieldErrors.name ? nameErrorId : undefined,
                  )}
                  onChange={(event) => setName(event.target.value)}
                />
                {fieldErrors.name ? (
                  <FieldError id={nameErrorId} message={fieldErrors.name} />
                ) : null}
              </>
            )}
          </Field>

          {isVariant ? (
            <Field
              label="Parent pathway"
              description="The family default this variant belongs to. It is stored as an explicit identity and cannot be changed after the variant exists."
            >
              {({ inputId, describedBy }) => (
                <>
                  <Select
                    id={inputId}
                    value={parentPathwayId}
                    aria-invalid={
                      fieldErrors.parentPathwayId !== undefined ||
                      parentUnavailable ||
                      undefined
                    }
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      fieldErrors.parentPathwayId || parentUnavailable
                        ? parentErrorId
                        : undefined,
                    )}
                    onChange={(event) => setParentPathwayId(event.target.value)}
                  >
                    <option value="">Choose a pathway…</option>
                    {parents.map((pathway) => (
                      <option key={pathway.id} value={pathway.id}>
                        {pathway.name}
                      </option>
                    ))}
                  </Select>
                  {fieldErrors.parentPathwayId ? (
                    <FieldError
                      id={parentErrorId}
                      message={fieldErrors.parentPathwayId}
                    />
                  ) : parentUnavailable ? (
                    <FieldError
                      id={parentErrorId}
                      message="The selected parent is archived or no longer available. Choose an active pathway."
                    />
                  ) : null}
                </>
              )}
            </Field>
          ) : null}

          <Field
            label="Pack placement"
            description={
              isVariant
                ? "The parent pathway's own coordinate, then '/', then this variant's slug — for example 'anxiety/agoraphobia'. Leave it empty if the parent has no coordinate of its own, as the base library's do. It cannot be changed after the asset exists."
                : "The folder this pathway's files take in a pack, as one slug — for example 'anxiety'. It cannot be changed after the asset exists."
            }
          >
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={placementCoordinate}
                  aria-invalid={
                    fieldErrors.placementCoordinate !== undefined || undefined
                  }
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    fieldErrors.placementCoordinate
                      ? placementErrorId
                      : undefined,
                  )}
                  onChange={(event) =>
                    setPlacementCoordinate(event.target.value)
                  }
                />
                {fieldErrors.placementCoordinate ? (
                  <FieldError
                    id={placementErrorId}
                    message={fieldErrors.placementCoordinate}
                  />
                ) : null}
              </>
            )}
          </Field>

          {!copyFrom ? (
            <Field
              label="Starting content"
              description={`Copies the exact current immutable version of an existing ${typeLabel}. Protected clinical-engine documents are preserved instead of fabricated.`}
            >
              {({ inputId, describedBy }) => (
                <>
                  <Select
                    id={inputId}
                    value={sourceId}
                    aria-invalid={
                      sourceUnavailable || sourceError !== undefined || undefined
                    }
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      sourceUnavailable || sourceError
                        ? sourceErrorId
                        : undefined,
                    )}
                    onChange={(event) => setSourceId(event.target.value)}
                  >
                    <option value="">Choose starting content…</option>
                    {sourceCandidates.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name} · {candidate.latestVersion}
                      </option>
                    ))}
                  </Select>
                  {/* The catalog's own staleness first: it is the reachable one
                      and it names the recovery. A `copiedFrom` refusal is the
                      service disagreeing about the version this dialog read the
                      content at (#954) — it should not happen, and when it does
                      it belongs on the control the clinician would look at
                      rather than in a sentence with no field. */}
                  {sourceUnavailable ? (
                    <FieldError
                      id={sourceErrorId}
                      message="The selected starting content is no longer active. Choose another asset."
                    />
                  ) : sourceError ? (
                    <FieldError id={sourceErrorId} message={sourceError} />
                  ) : null}
                </>
              )}
            </Field>
          ) : null}

          {!copyFrom && catalog.isSuccess && sourceCandidates.length === 0 ? (
            <UnavailableState
              title="No starting content is available"
              description={`Creating a ${typeLabel} requires an existing same-type version so protected clinical-engine documents are preserved. Add or seed one before creating another.`}
            />
          ) : null}

          {prerequisite.state === "pending" ? (
            <LoadingState
              label={
                prerequisite.purpose === "source"
                  ? `Loading exact content from ${sourceAsset?.name ?? "the selected asset"}`
                  : "Loading available Pathways"
              }
              rows={2}
            />
          ) : null}

          {prerequisite.state === "failed" ? (
            <NamedReadFailureState
              failure={prerequisite.failure}
              isPaused={prerequisite.isPaused}
              retry={prerequisite.retry}
              subject={
                prerequisite.purpose === "source"
                  ? "Asset content"
                  : "Clinical library"
              }
              title={
                prerequisite.purpose === "source"
                  ? "Starting content could not be read"
                  : "Available Pathways could not be read"
              }
              description={
                prerequisite.purpose === "source"
                  ? `The Editor Service did not return ${sourceAsset?.name ?? "the selected asset"} at ${sourceAsset?.latestVersion ?? "the selected version"}, so exact starting content is unavailable.`
                  : "The Editor Service did not return the active assets that may supply starting content or own this variant."
              }
              deniedDescription={
                prerequisite.purpose === "source"
                  ? "You are not authorized to read this asset's content, so it cannot be used as a source."
                  : "You are not authorized to read the active Pathways available for this create operation."
              }
              offlineDescription={
                prerequisite.purpose === "source"
                  ? `${sourceAsset?.name ?? "The source asset"} at ${sourceAsset?.latestVersion ?? "the selected version"} could not be reached, so exact starting content is unavailable. It loads on its own when the connection returns.`
                  : "The active Pathways could not be reached, so starting content and a parent cannot be chosen safely. They load on their own when the connection returns."
              }
            />
          ) : null}

          {generalMessage ? (
            <Notice tone="destructive" role="alert">
              {generalMessage}
            </Notice>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
